import { z } from 'zod';
import { booleanParam, PaginationMetaSchema } from './pagination.js';

// ----------------------------------------------------------------
// GeoJSON building blocks (added 2026-04-21 for the polygon editor)
// ----------------------------------------------------------------

/**
 * Single [lon, lat] position. GeoJSON uses lon-first; we enforce it here
 * to catch a common footgun. An optional altitude is accepted per RFC
 * 7946 but discarded — this product is 2D only.
 */
export const GeoJsonPositionSchema = z
  .tuple([
    z.number().gte(-180).lte(180),
    z.number().gte(-90).lte(90),
  ])
  .rest(z.number())
  .transform((t) => [t[0], t[1]] as [number, number]);

/**
 * A linear ring: at least 4 positions (3 distinct + closing duplicate)
 * with the first and last points equal.
 */
export const GeoJsonLinearRingSchema = z
  .array(GeoJsonPositionSchema)
  .min(4, 'ring requires at least 3 distinct points plus closing point')
  .refine(
    (ring) => {
      const a = ring[0]!;
      const b = ring[ring.length - 1]!;
      return a[0] === b[0] && a[1] === b[1];
    },
    { message: 'ring not closed (first point != last point)' },
  );

export const GeoJsonPolygonSchema = z
  .object({
    type: z.literal('Polygon'),
    coordinates: z.array(GeoJsonLinearRingSchema).min(1),
  })
  .strict();
export type GeoJsonPolygon = z.infer<typeof GeoJsonPolygonSchema>;

export const GeoJsonMultiPolygonSchema = z
  .object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(z.array(GeoJsonLinearRingSchema).min(1)).min(1),
  })
  .strict();
export type GeoJsonMultiPolygon = z.infer<typeof GeoJsonMultiPolygonSchema>;

export const GeoJsonPolygonLikeSchema = z.union([
  GeoJsonPolygonSchema,
  GeoJsonMultiPolygonSchema,
]);
export type GeoJsonPolygonLike = z.infer<typeof GeoJsonPolygonLikeSchema>;

// ----------------------------------------------------------------
// Road overlays (FeatureCollection<LineString>)
// ----------------------------------------------------------------

export const RoadLineStringFeatureSchema = z
  .object({
    type: z.literal('Feature'),
    geometry: z
      .object({
        type: z.literal('LineString'),
        coordinates: z.array(GeoJsonPositionSchema).min(2),
      })
      .strict(),
    properties: z
      .object({
        name: z.string().max(100).optional(),
        widthM: z.number().positive().lte(50).optional(),
      })
      .strict()
      .default({}),
  })
  .strict();
export type RoadLineStringFeature = z.infer<typeof RoadLineStringFeatureSchema>;

export const RoadsFeatureCollectionSchema = z
  .object({
    type: z.literal('FeatureCollection'),
    features: z.array(RoadLineStringFeatureSchema).max(200),
  })
  .strict();
export type RoadsFeatureCollection = z.infer<typeof RoadsFeatureCollectionSchema>;

// ----------------------------------------------------------------
// Parcel DTO (read side)
// ----------------------------------------------------------------

export const ParcelSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  importBatchId: z.string().uuid().nullable(),
  projectUcfKey: z.string(),
  sequenceId: z.number().int().nullable(),
  projectId: z.string().nullable(),
  raion: z.string(),
  primarie: z.string(),
  entitateSilvica: z.string().nullable(),
  codProiect: z.string().nullable(),
  suprafata: z.number(),
  ucf: z.string(),
  ocolSilvic: z.string().nullable(),
  anul: z.number().int(),
  geometryType: z.string().nullable(),
  geometry: z.unknown().nullable(),
  roads: z.unknown().nullable(),
  /** Optional plot-level identifiers populated by the shapefile import
   * (UCF_PNERP_2022_2025). The xlsx (meta_3) flow leaves them NULL. */
  parcelaNumber: z.string().nullable().optional(),
  ua: z.string().nullable().optional(),
  codPrimarie: z.string().nullable().optional(),
  /** Live subpolygons of this parcel. Set by the list endpoint only. */
  subParcelCount: z.number().int().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type ParcelDto = z.infer<typeof ParcelSchema>;

export const ParcelListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  raion: z.string().optional(),
  raioane: z.string().optional(), // csv; takes precedence over `raion`
  primarie: z.string().optional(),
  primarii: z.string().optional(), // csv; takes precedence over `primarie`
  ocolSilvic: z.string().optional(),
  ocoaleSilvice: z.string().optional(), // csv; takes precedence over `ocolSilvic`
  entitateSilvica: z.string().optional(),
  entitatiSilvice: z.string().optional(), // csv; takes precedence over `entitateSilvica`
  anul: z.coerce.number().int().optional(),
  years: z.string().optional(), // csv; takes precedence over `anul`
  anulMin: z.coerce.number().int().optional(),
  anulMax: z.coerce.number().int().optional(),
  codProiect: z.string().optional(),
  codProiecte: z.string().optional(), // csv; takes precedence over `codProiect`
  ucf: z.string().optional(),
  ucfs: z.string().optional(), // csv; takes precedence over `ucf`
  suprafataMin: z.coerce.number().optional(),
  suprafataMax: z.coerce.number().optional(),
  search: z.string().optional(),
  /** true = only parcels with subpolygons, false = only those without. */
  hasSubParcels: booleanParam.optional(),
  /**
   * When true, the list includes soft-deleted parcels (scoped the same
   * way as live rows: workers see only their own). Used by the UI to
   * offer a "Restore" action on recently-deleted parcels.
   */
  includeDeleted: z.coerce.boolean().optional().default(false),
  sort: z
    .enum([
      'createdAt',
      'anul',
      'raion',
      'primarie',
      'suprafata',
      'ocolSilvic',
      'entitateSilvica',
      'codProiect',
      'ucf',
    ])
    .default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type ParcelListQuery = z.infer<typeof ParcelListQuerySchema>;

export const ParcelListResponseSchema = z.object({
  items: z.array(ParcelSchema),
  pagination: PaginationMetaSchema,
  totals: z.object({
    totalSuprafata: z.number(),
    distinctRaioane: z.number().int(),
    distinctAni: z.number().int(),
  }),
});
export type ParcelListResponse = z.infer<typeof ParcelListResponseSchema>;

/**
 * Payload for manual parcel entry via `POST /api/parcels`. Matches the
 * shape workers type into the "Parcel nou" form dialog. Required fields
 * mirror the import engine's REQUIRED_CANONICAL_FIELDS plus the natural
 * key. Geometry is optional at the schema level — metadata-only creates
 * remain valid; the editor sends canonical GeoJSON Polygon/MultiPolygon.
 */
export const ParcelCreateInputSchema = z
  .object({
    projectUcfKey: z
      .string()
      .min(1, 'projectUcfKey required')
      .max(200, 'projectUcfKey too long'),
    sequenceId: z.number().int().nullable().optional(),
    projectId: z.string().max(50).nullable().optional(),
    raion: z.string().min(1, 'raion required').max(100),
    primarie: z.string().min(1, 'primarie required').max(100),
    entitateSilvica: z.string().max(100).nullable().optional(),
    codProiect: z.string().max(200).nullable().optional(),
    suprafata: z
      .number()
      .nonnegative('suprafata must be >= 0')
      .max(1e6, 'suprafata unrealistic'),
    ucf: z.string().min(1, 'ucf required').max(50),
    ocolSilvic: z.string().max(100).nullable().optional(),
    anul: z.number().int().min(1900).max(2200),
    geometryType: z.enum(['Polygon', 'MultiPolygon']).nullable().optional(),
    geometry: GeoJsonPolygonLikeSchema.nullable().optional(),
    roads: RoadsFeatureCollectionSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.geometry == null ||
      v.geometryType == null ||
      v.geometry.type === v.geometryType,
    {
      message: 'geometryType does not match geometry.type',
      path: ['geometryType'],
    },
  );
export type ParcelCreateInput = z.infer<typeof ParcelCreateInputSchema>;

/**
 * Partial update for `PATCH /api/parcels/:id`. Every field is optional;
 * at least one must be present. `projectUcfKey` is intentionally NOT
 * editable — it is the natural key and must stay stable for dedup.
 *
 * Extra fields over `ParcelCreateInput`:
 *  - `baseUpdatedAt` — ISO-8601 captured at editor-open; enables 409
 *    last-write-wins concurrency check (ADR-POLY-006). Optional to keep
 *    metadata-only callers backward compatible.
 *  - `syncSuprafata` — when true, the server persists `body.suprafata`
 *    even if no other metadata changed (FR-07 opt-in). When absent or
 *    false the server keeps the prior `suprafata` regardless of what
 *    the client computed.
 */
export const ParcelUpdateInputSchema = ParcelCreateInputSchema
  .innerType()
  .partial()
  .omit({ projectUcfKey: true })
  .extend({
    baseUpdatedAt: z.string().datetime({ offset: true }).optional(),
    syncSuprafata: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
  });
export type ParcelUpdateInput = z.infer<typeof ParcelUpdateInputSchema>;
